import { useRef, useState } from "react";
import { SweepButton } from "@softmax/design-system";
import type { ProfileNotice } from "../profile-notice";
import { fieldLabel, bodyText, inputStyle } from "./profile-styles";
import { ProfileStatus } from "./profile-status";

export function ProfileField({
  id,
  label,
  saved,
  draft,
  onDraft,
  onSave,
  onCancel,
  status,
  disabled = false,
  required = false,
  type = "text",
  autoComplete,
  maxLength,
  saveLabel = "Save",
  emptyText = "Not set",
}: {
  id: string;
  label: string;
  saved: string;
  draft: string;
  onDraft: (value: string) => void;
  onSave: (input: HTMLInputElement) => Promise<boolean>;
  onCancel: () => void;
  status: ProfileNotice;
  disabled?: boolean;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
  saveLabel?: string;
  emptyText?: string;
}) {
  const [editing, setEditing] = useState(false);
  const open = editing || (saved === "" && !disabled);
  const input = useRef<HTMLInputElement>(null);
  const focusRequested = useRef(false);
  const close = () => {
    if (disabled) return;

    setEditing(false);
    onCancel();
  };
  const submit = async (field: HTMLInputElement) => {
    if (disabled) return;

    if (await onSave(field)) setEditing(false);
  };
  return (
    <div className="flex flex-col gap-[6px]">
      <label htmlFor={id} className={fieldLabel}>
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px]">
        {open ? (
          <>
            <input
              ref={(node) => {
                input.current = node;

                if (node && focusRequested.current) {
                  node.focus();
                  focusRequested.current = false;
                }
              }}
              id={id}
              name={id}
              type={type}
              required={required}
              autoComplete={autoComplete}
              maxLength={maxLength}
              className={inputStyle}
              disabled={disabled}
              value={draft}
              onChange={(event) => onDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && input.current) {
                  event.preventDefault();
                  void submit(input.current);
                } else if (event.key === "Escape") close();
              }}
            />
            <SweepButton
              quiet
              type="button"
              focus="ring"
              disabled={disabled}
              onClick={() => {
                if (input.current) void submit(input.current);
              }}
            >
              {saveLabel}
            </SweepButton>
            {saved !== "" ? (
              <SweepButton
                quiet
                type="button"
                focus="ring"
                disabled={disabled}
                onClick={close}
              >
                Cancel
              </SweepButton>
            ) : null}
          </>
        ) : (
          <>
            <span id={id} className={`${bodyText} text-[#161616]`}>
              {saved || emptyText}
            </span>
            {disabled ? null : (
              <SweepButton
                quiet
                type="button"
                focus="ring"
                aria-label={`Edit ${label.toLowerCase()}`}
                onClick={() => {
                  focusRequested.current = true;
                  setEditing(true);
                }}
              >
                Edit
              </SweepButton>
            )}
          </>
        )}
        <ProfileStatus notice={status} />
      </div>
    </div>
  );
}
